import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponseBase} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {defer} from 'lodash';
import {EMPTY, from, Observable, switchMap, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {toPromise} from 'src/app/domain/function/subscription.helper';
import {Auth, AuthGrantType} from 'src/app/domain/model/auth.model';
import AuthApi from 'src/app/domain/service/api/auth.api';
import RouteResolverService from 'src/app/domain/service/route-resolver/route-resolver.service';
import AuthHttpInterceptorStateStore, {AuthHttpInterceptorState} from 'src/app/domain/store/auth-http-interceptor-state.store';
import AuthStore from 'src/app/domain/store/auth.store';
import {UserSession} from 'src/app/infrastructure/environment/user-session';
import {SKIP_GLOBAL_ERROR_REDIRECT} from 'src/app/infrastructure/http/http-context';
import {isNotInternetConnection, isServerError, isSuccess, isUnauthorized} from 'src/app/infrastructure/http/http-response';

@Injectable({
  providedIn: 'root'
})
export class HttpErrorInterceptor implements HttpInterceptor {
  private static readonly refreshTokenWaitTimeoutMs = 6000;
  private static readonly retryCountHeader = 'X-Auth-Retry-Count';
  private static readonly maxUnauthorizedRetries = 1;

  private me: Auth | null;
  private state: AuthHttpInterceptorState;

  constructor(
    private readonly userSession: UserSession,
    private readonly authStore: AuthStore,
    private readonly authApi: AuthApi,
    private readonly routeResolver: RouteResolverService,
    private readonly authHttpInterceptorStore: AuthHttpInterceptorStateStore,
    private readonly router: Router
  ) {
    this.authStore.me$.subscribe((auth) => this.me = auth);
    this.authHttpInterceptorStore.state$.subscribe(state => this.state = state);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshTokenRequest(request)) {
      if (this.state.submittingRefreshToken) {
        return EMPTY;
      }
      this.incrementRefreshTokenRetry();
      this.setSubmittingTokenRefresh();
    }

    if (this.isExternalRequest(request)) {
      return next.handle(request);
    }

    const authedRequest = this.addAuthHeaders(request);
    return next.handle(authedRequest).pipe(
      tap((response) => this.recalculateState(request, response)),
      catchError((errorResponse) => {
        this.recalculateState(request, errorResponse);

        if (this.isRefreshTokenRequest(request)) {
          this.resetRefreshTokenState();
        }

        if (isServerError(errorResponse) || isNotInternetConnection(errorResponse)) {
          if (request.context.get(SKIP_GLOBAL_ERROR_REDIRECT)) {
            return throwError(() => errorResponse);
          }
          return this.handleInternalServerError(errorResponse);
        }

        if (isUnauthorized(errorResponse)) {
          return this.handleUnauthorizedError(errorResponse, request, next);
        }

        return throwError(() => errorResponse);
        }
      )
    );
  }

  private setSubmittingTokenRefresh() {
    this.authHttpInterceptorStore.save({
      ...this.state,
      submittingRefreshToken: true,
    });
  }

  private incrementRefreshTokenRetry() {
    this.authHttpInterceptorStore.save({
      ...this.state,
      refreshTokenRetries: this.state.refreshTokenRetries + 1
    });
  }

  private recalculateState(request: HttpRequest<any>, response: HttpResponseBase | any) {
    if (response instanceof HttpResponseBase && this.isRefreshTokenRequest(request)) {
      this.authHttpInterceptorStore.save({
        submittingRefreshToken: false,
        refreshTokenRetries: isSuccess(response) ? 0 : this.state.refreshTokenRetries,
        isValidAccessToken: isSuccess(response)
      });
    }
  }

  private handleUnauthorizedError(errorResponse, request: HttpRequest<any>, next: HttpHandler) {
    const isFirstUnauthorized = this.state.isValidAccessToken;
    this.invalidateStateAccessToken();

    if (this.isLoginRequest(request) || this.isRegisterRequest(request)) {
      return throwError(() => errorResponse);
    }

    if (!this.userSession.isLogged()) {
      this.handleLogout();
      return throwError(() => errorResponse);
    }

    if (this.isRefreshTokenRequest(request)) {
      this.resetRefreshTokenState();
      this.handleLogout();
      return throwError(() => errorResponse);
    }

    if (this.retryCount(request) >= HttpErrorInterceptor.maxUnauthorizedRetries) {
      this.handleLogout();
      return throwError(() => errorResponse);
    }

    isFirstUnauthorized && defer(() => this.authApi.refreshToken());

    const retriedRequest = this.markRetry(request);

    return from(this.waitToValidAccessToken()).pipe(
      switchMap(() => this.intercept(retriedRequest, next)),
      catchError((waitError) => {
        if (!this.userSession.isLogged()) {
          this.handleLogout();
        }
        return throwError(() => waitError ?? errorResponse);
      })
    );
  }

  private resetRefreshTokenState(): void {
    this.authHttpInterceptorStore.save({
      isValidAccessToken: false,
      submittingRefreshToken: false,
      refreshTokenRetries: 0
    });
  }

  private invalidateStateAccessToken() {
    this.authHttpInterceptorStore.save({
      ...this.state,
      isValidAccessToken: false
    });
  }

  private async waitToValidAccessToken(): Promise<void> {
    await Promise.race([
      toPromise(this.authHttpInterceptorStore.state$, (state) => state.isValidAccessToken === true),
      new Promise((_, reject) => setTimeout(
        () => reject(new Error('Refresh token wait timeout')),
        HttpErrorInterceptor.refreshTokenWaitTimeoutMs
      ))
    ]);
  }

  private async handleLogout(): Promise<void> {
    await this.authApi.logout();
    defer(() => this.router.navigate([this.routeResolver.resolve()]));
  }

  private retryCount(request: HttpRequest<any>): number {
    return Number(request.headers.get(HttpErrorInterceptor.retryCountHeader) ?? 0);
  }

  private markRetry(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({
      setHeaders: {[HttpErrorInterceptor.retryCountHeader]: String(this.retryCount(request) + 1)}
    });
  }

  private isLoginRequest(request: HttpRequest<any>): boolean {
    return request.url.endsWith('/login') && !request.params.has('grant_type');
  }

  private isRegisterRequest(request: HttpRequest<any>): boolean {
    return request.url.endsWith('/register');
  }

  private isRefreshTokenRequest(request: HttpRequest<any>): boolean {
    return request.url.endsWith('/login') && request.params.get('grant_type') === AuthGrantType.refreshToken;
  }

  private isExternalRequest(request: HttpRequest<any>): boolean {
    return request.url.startsWith('https://api.ipify.org') || request.url.startsWith('http://api.ipify.org');
  }

  private handleInternalServerError(error: any) {
    defer(() => this.router.navigate([this.routeResolver.resolveErrorUrl()]));
    return throwError(() => error);
  }

  private addAuthHeaders(request: HttpRequest<any>) {
    if (request.headers.has(HttpErrorInterceptor.retryCountHeader)) {
      request = request.clone({headers: request.headers.delete(HttpErrorInterceptor.retryCountHeader)});
    }

    if (this.me?.accessToken) {
      request = request.clone({
        setHeaders: {
          authorization: 'Bearer ' + this.me.accessToken
        }
      });
    }
    return request;
  }
}
